import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Shield, Lock, UserCog, Key, BookOpen, CreditCard, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const VERSION = "V3";
const CACHE_BUSTER = `v=${Date.now()}`;

const allSubjects = {
  tgt: ["hindi","english","mathematics","science","social_science","computer_science","physical_education","music","art"],
  pgt: ["hindi","english","mathematics","physics","chemistry","biology","commerce","economics","geography","history","computer_science","music","art"],
  non_teaching: ["hostel_warden","library_science"]
};

export default function SupportPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('support_authenticated') === 'true');
  const [password, setPassword] = useState("");
  const [supportKey, setSupportKey] = useState(() => localStorage.getItem('support_key') || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState("");

  // Password change
  const [newPassword, setNewPassword] = useState("");
  // Grant access
  const [grantMobile, setGrantMobile] = useState("");
  // Course change
  const [courseExam, setCourseExam] = useState("");
  const [courseSubject, setCourseSubject] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('password', password);
      const res = await axios.post(`${API}/support/login`, formData);
      if (res.data.success) {
        setIsAuthenticated(true);
        setSupportKey(password);
        localStorage.setItem('support_authenticated', 'true');
        localStorage.setItem('support_key', password);
        toast.success("Support access granted");
      }
    } catch {
      toast.error("Invalid support password");
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      toast.error("Enter at least 2 characters to search");
      return;
    }
    setSearching(true);
    setSelectedUser(null);
    try {
      const res = await axios.get(`${API}/support/search?${CACHE_BUSTER}`, { params: { support_key: supportKey, q: searchQuery.trim() } });
      setResults(res.data.results);
      if (res.data.count === 0) toast("No users found");
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || newPassword.trim().length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    setActionLoading("password");
    try {
      const res = await axios.post(`${API}/support/change-password`, {
        support_key: supportKey, user_id: selectedUser.id, new_password: newPassword.trim()
      });
      toast.success(res.data.message + (res.data.email_sent ? " | Email sent!" : " | No email sent (no email on file)"));
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setActionLoading("");
    }
  };

  const handleChangeCourse = async () => {
    setActionLoading("course");
    try {
      const res = await axios.post(`${API}/support/change-course`, {
        support_key: supportKey, user_id: selectedUser.id,
        exam_type: courseExam || null, subject: courseSubject || null
      });
      toast.success(res.data.message);
      setCourseExam(""); setCourseSubject("");
      // Refresh user data
      handleSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setActionLoading("");
    }
  };

  const handleGrantAccess = async () => {
    if (!grantMobile.trim()) {
      toast.error("Enter mobile number");
      return;
    }
    setActionLoading("grant");
    try {
      const res = await axios.post(`${API}/support/grant-access`, {
        support_key: supportKey, mobile: grantMobile.trim()
      });
      toast.success(res.data.message);
      setGrantMobile("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setActionLoading("");
    }
  };

  const getPlanBadge = (plan) => {
    if (plan === 'lokesh_notes') return <Badge className="bg-green-600 text-white">Premium</Badge>;
    if (plan === 'unlimited') return <Badge className="bg-yellow-500 text-white">Unlimited</Badge>;
    if (plan === 'basic') return <Badge className="bg-blue-500 text-white">Basic</Badge>;
    if (plan === 'standard') return <Badge className="bg-purple-500 text-white">Standard</Badge>;
    return <Badge variant="outline">Free</Badge>;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Support Panel</h1>
              <p className="text-gray-400 text-sm">Enter support team password</p>
              <Badge className="mt-2 bg-blue-600/30 text-blue-300 border-blue-500/50">{VERSION}</Badge>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Support Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                data-testid="support-password-input"
              />
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="support-login-btn">
                Access Panel
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-white">Back to Home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <nav className="bg-black/30 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-400" />
            <span className="font-bold text-white text-lg">Support Panel</span>
            <Badge className="bg-blue-600/30 text-blue-300 border-blue-500/50 text-xs">{VERSION}</Badge>
          </div>
          <div className="flex gap-2">
            <Link to="/admin"><Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 text-xs">Admin</Button></Link>
            <Link to="/"><Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10 text-xs">Home</Button></Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="py-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, mobile or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-white/10 border-white/20 text-white placeholder-gray-400 flex-1"
                data-testid="support-search-input"
              />
              <Button onClick={handleSearch} disabled={searching} className="bg-blue-600 hover:bg-blue-700" data-testid="support-search-btn">
                <Search className="w-4 h-4 mr-2" />{searching ? "..." : "Search"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Grant Paid Access - Quick Action */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-green-400" />Grant Paid Access by Mobile Number
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3 px-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter mobile number..."
                value={grantMobile}
                onChange={(e) => setGrantMobile(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder-gray-400 flex-1"
                data-testid="grant-mobile-input"
              />
              <Button onClick={handleGrantAccess} disabled={actionLoading === "grant"} className="bg-green-600 hover:bg-green-700" data-testid="grant-access-btn">
                <CreditCard className="w-4 h-4 mr-2" />{actionLoading === "grant" ? "..." : "Grant Access"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Search Results */}
          <div className="lg:col-span-2">
            {results.length > 0 && (
              <Card className="bg-white/5 border-white/10">
                <CardHeader className="py-3 px-4 border-b border-white/10">
                  <CardTitle className="text-white text-sm">{results.length} Results</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[60vh] overflow-y-auto">
                    {results.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => { setSelectedUser(user); setNewPassword(""); setCourseExam(""); setCourseSubject(""); }}
                        className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition ${
                          selectedUser?.id === user.id ? 'bg-blue-500/20 border-l-4 border-l-blue-400' : ''
                        }`}
                        data-testid={`user-result-${user.mobile}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium text-sm">{user.name}</p>
                            <p className="text-gray-400 text-xs">{user.mobile} {user.email ? `| ${user.email}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getPlanBadge(user.plan)}
                            <span className="text-xs text-gray-500">{user.questions_used}/{user.questions_limit === -1 ? '~' : user.questions_limit}</span>
                          </div>
                        </div>
                        {user.enrolled_notes_course && (
                          <p className="text-xs text-amber-400 mt-1">
                            Notes: {user.enrolled_notes_course.exam_type?.toUpperCase()} {user.enrolled_notes_course.subject?.replace(/_/g,' ')}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* User Actions Panel */}
          <div>
            {selectedUser ? (
              <div className="space-y-4">
                {/* User Info */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="py-4 px-4">
                    <h3 className="text-white font-bold mb-2">{selectedUser.name}</h3>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-400">Mobile: <span className="text-white">{selectedUser.mobile}</span></p>
                      <p className="text-gray-400">Email: <span className="text-white">{selectedUser.email || 'N/A'}</span></p>
                      <p className="text-gray-400">Plan: {getPlanBadge(selectedUser.plan)}</p>
                      <p className="text-gray-400">Questions: <span className="text-white">{selectedUser.questions_used}/{selectedUser.questions_limit === -1 ? 'Unlimited' : selectedUser.questions_limit}</span></p>
                      {selectedUser.enrolled_notes_course && (
                        <p className="text-gray-400">Notes Course: <span className="text-amber-400">{selectedUser.enrolled_notes_course.exam_type?.toUpperCase()} {selectedUser.enrolled_notes_course.subject?.replace(/_/g,' ')}</span></p>
                      )}
                      <p className="text-gray-400">Email Sent: {selectedUser.welcome_email_sent ? <CheckCircle className="w-3 h-3 inline text-green-400" /> : <span className="text-red-400">No</span>}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Change Password */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <Key className="w-4 h-4 text-amber-400" />Change Password
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-3 px-4 space-y-2">
                    <Input
                      placeholder="New password..."
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                      data-testid="new-password-input"
                    />
                    <Button onClick={handleChangePassword} disabled={actionLoading === "password"} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="change-password-btn">
                      <Mail className="w-4 h-4 mr-2" />{actionLoading === "password" ? "Changing..." : "Change & Email"}
                    </Button>
                    <p className="text-xs text-gray-500">New password will be emailed to user</p>
                  </CardContent>
                </Card>

                {/* Change Notes Course */}
                <Card className="bg-white/5 border-white/10">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-blue-400" />Change Notes Course
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-3 px-4 space-y-2">
                    <Select value={courseExam} onValueChange={(v) => { setCourseExam(v); setCourseSubject(""); }}>
                      <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Exam Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tgt">TGT</SelectItem>
                        <SelectItem value="pgt">PGT</SelectItem>
                        <SelectItem value="non_teaching">Non-Teaching</SelectItem>
                      </SelectContent>
                    </Select>
                    {courseExam && (
                      <Select value={courseSubject} onValueChange={setCourseSubject}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue placeholder="Subject" /></SelectTrigger>
                        <SelectContent>
                          {(allSubjects[courseExam] || []).map(s => (
                            <SelectItem key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <div className="flex gap-2">
                      <Button onClick={handleChangeCourse} disabled={actionLoading === "course"} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="change-course-btn">
                        {actionLoading === "course" ? "..." : "Update Course"}
                      </Button>
                      <Button onClick={() => { setCourseExam(""); setCourseSubject(""); handleChangeCourse(); }} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="clear-course-btn">
                        Clear
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-12 text-center">
                  <UserCog className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400 text-sm">Search and select a user to manage</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
